import { Container, Point } from 'pixi.js';
import type { CustomTaskBubble } from './TaskScoreOverlay';
import { BirdTaskBubbleLayout } from '../../../layouts/scoring/BirdTaskBubbleLayout';
import MathUtils from '../../utils/MathUtils';

export class BirdTaskBubbleView extends Container implements CustomTaskBubble {
  private readonly layout = new BirdTaskBubbleLayout();

  constructor() {
    super();

    const { root, musicNote1, musicNote2 } = this.layout;

    root.props({
      parent: this,
    });

    musicNote1.props({
      scale: 0,
      alpha: 0,
    });

    musicNote2.props({
      scale: 0,
      alpha: 0,
    });
  }

  public animateTo(target: Point, onComplete: () => void): void {
    const { leftWing, rightWing } = this.layout;

    leftWing.tweenTo({ angle: -25 }, 100, 'quadOut', true, 0, -1, true);
    rightWing.tweenTo({ angle: 25 }, 100, 'quadOut', true, 0, -1, true);

    this.tweenFrom({ alpha: 0, scaleX: 0.25, scaleY: 0.52 }, 200, 'quadOut', true);

    const hoverX = this.position.x + MathUtils.getRandomValueInRange(-30, 30);
    const hoverY = this.position.y - 120;

    // Move to hover position, then fly to task goal.
    this.tweenTo({ x: hoverX, y: hoverY }, 500, 'quadOut', true).onComplete.addOnce(() => {
      this.startMusicNoteAnimations();
      this.startFlyingMovement(target, 800, 1000, onComplete);
    });
  }

  private startFlyingMovement(to: Point, duration: number, delay: number, onComplete: () => void): void {
    const from = this.position.clone();

    this.props({
      x: from.x,
      y: from.y,
      anchor: 0.5,
    });

    const animationData = {
      time: 0,
    };

    const controlPoint = new Point(from.x + (to.x - from.x) / 2, from.y + (to.y - from.y) / 2);
    const distance = MathUtils.getDistance(from.x, from.y, to.x, to.y);
    const controlPointOffsetX = distance * MathUtils.getRandomValueInRange(0.33, 0.37);
    controlPoint.x -= Math.sign(from.x - to.x) * controlPointOffsetX;

    const updateFlyingMovement = (): void => {
      const curveXY = MathUtils.getBezierCurvePosition(from, controlPoint, to, animationData.time);
      this.x = curveXY.x;
      this.y = curveXY.y;

      if (animationData.time > 0.9) {
        this.alpha = (1 - animationData.time) / 0.1;
      }
    };

    const tween = this.tweenCreate(animationData).to({ time: 1 }, duration, 'quadInOut', true, delay);
    tween.onUpdate.add(updateFlyingMovement);
    tween.onComplete.add((): void => {
      updateFlyingMovement();
      onComplete();
    });
  }

  private startMusicNoteAnimations(): void {
    const { musicNote1, musicNote2 } = this.layout;

    const animationData = { animationTime: 0 };

    musicNote1.props({
      y: musicNote1.y + 10,
    });

    musicNote2.props({
      y: musicNote2.y + 10,
    });

    musicNote1.tweenTo({ scaleX: 1, scaleY: 1, alpha: 1, y: musicNote1.y - 10 }, 250, 'backOut', true);
    musicNote2.tweenTo({ scaleX: 1, scaleY: 1, alpha: 1, y: musicNote2.y - 10 }, 250, 'backOut', true, 200);

    const shakeTween = this.tweenCreate(animationData).to({ animationTime: 1 }, 1500, 'linear', true);

    shakeTween.onUpdate.add(() => {
      musicNote1.angle = Math.sin(animationData.animationTime * 15) * 5;
      musicNote2.angle = Math.sin(animationData.animationTime * 15) * 4;
    });

    musicNote1.tweenTo({ scaleX: 0.5, scaleY: 0.5, alpha: 0 }, 700, 'backOut', true, 800);
    musicNote2.tweenTo({ scaleX: 0.5, scaleY: 0.5, alpha: 0 }, 700, 'backOut', true, 1000);
  }
}
